import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Navigation from '@/public/images/ackodrive/Navigation.svg';
import Image from 'next/image';
import { resolvePath } from '@/utils/resolvePath';
import { useRouter } from 'next/router';
import { imageLoader } from '@/utils/imageLoader';
import { trackEventAPI } from '@/analytics/middleware';
import {
    TAP_BTN_SERVICE_CENTRE_SELECTED,
    TAP_BTN_SERVICE_CENTRE_LOCATION,
} from '@/analytics/events';
import { openMapLink } from '@/utils';

const OurServiceCentres = ({
    heading,
    garageList,
    isCity,
    brand,
    garage,
    isComingSoon,
}: {
    heading?: string;
    garageList: any;
    isCity?: any;
    brand?: any;
    garage?: any;
    isComingSoon?: boolean;
}) => {
    const router = useRouter();
    const [showViewAll, setShowViewAll] = useState(false);

    useEffect(() => {
        if (!isComingSoon && garageList?.length > 2) {
            setShowViewAll(true);
        }
    }, []);

    const showAllData = () => {
        setShowViewAll(false);
    };

    const handleClick = async (mapLink: any, serviceCentreName: string) => {
        await trackEventAPI({
            event_name: TAP_BTN_SERVICE_CENTRE_LOCATION,
            customAttributes: {
                service_centre_name: serviceCentreName || '',
            },
            isFakeDoor: true,
        });
        openMapLink(mapLink);
    };

    const createLink = (garageName: string) => {
        if (
            process.env.NEXT_PUBLIC_ENV_PREFIX &&
            garageName.includes(process.env.NEXT_PUBLIC_ENV_PREFIX)
        ) {
            garageName = garageName.replace(
                process.env.NEXT_PUBLIC_ENV_PREFIX,
                ''
            );
        }
        garageName = garageName.replace('/delhi/', '/new-delhi/');
        let finalGarageUrl;
        if (isCity) {
            finalGarageUrl = garageName;
        } else if (brand) {
            const { garageOrBrand = '' }: any = router.query;
            let garagePath: any = garageName.split('/');
            let brandService = garageOrBrand.split('-in-');
            let name = garagePath[2];
            garagePath[2] = brandService[0] + '-in-' + name;
            garagePath = garagePath.join('/');
            finalGarageUrl = garagePath;
        } else if (garage || isComingSoon) {
            finalGarageUrl = garageName;
        }
        return `${process.env.NEXT_PUBLIC_ENV_PREFIX}${finalGarageUrl}`;
    };

    const handleCardClick = (serviceCentreName: string) => {
        trackEventAPI({
            event_name: TAP_BTN_SERVICE_CENTRE_SELECTED,
            customAttributes: {
                service_centre_name: serviceCentreName || '',
            },
            isFakeDoor: true,
        });
    };

    return (
        <OurServiceCentresWrapper isComingSoon={isComingSoon}>
            <OurServiceCentresContainer>
                {heading && (
                    <OurServiceCentresHeading
                        dangerouslySetInnerHTML={{ __html: heading }}
                    />
                )}
                <OurServiceCentresCardsWrapper>
                    {garageList.map((garage: any, index: number) => {
                        if (showViewAll && index > 1) {
                            return;
                        }
                        return (
                            <OurServiceCentresCardWrapper>
                                <OurServiceCentresCard>
                                    <TopPart>
                                        <Image
                                            loader={imageLoader}
                                            src={resolvePath(
                                                '/images/image289.jpg'
                                            )}
                                            layout="fill"
                                            alt="Service-center"
                                        />
                                    </TopPart>
                                    <BottomPart
                                        href={createLink(
                                            garage.garageLandingPath
                                        )}
                                        onClick={() =>
                                            handleCardClick(garage.garageName)
                                        }
                                    >
                                        <BottomPartLeft>
                                            <LocationWrapper>
                                                <Location>
                                                    {garage.garageName}
                                                </Location>
                                            </LocationWrapper>
                                            <AddressWrapper>
                                                <Address
                                                    isComingSoon={isComingSoon}
                                                >
                                                    {`Address: ${garage.garageAddress}`}
                                                </Address>
                                            </AddressWrapper>
                                        </BottomPartLeft>
                                        <Image
                                            src={resolvePath(
                                                '/images/seo/Chevron-right.svg'
                                            )}
                                            width={24}
                                            height={24}
                                            alt="Location"
                                        />
                                    </BottomPart>
                                </OurServiceCentresCard>
                                {!isComingSoon && (
                                    <ServiceCentreDirectionWrapper>
                                        <ServiceCentreDirectionContainer
                                            onClick={() =>
                                                handleClick(
                                                    garage.garageMapLink,
                                                    garage.garageName
                                                )
                                            }
                                        >
                                            <ServiceCentreDirectionText>
                                                Get directions
                                            </ServiceCentreDirectionText>
                                            <Navigation />
                                        </ServiceCentreDirectionContainer>
                                    </ServiceCentreDirectionWrapper>
                                )}
                            </OurServiceCentresCardWrapper>
                        );
                    })}
                </OurServiceCentresCardsWrapper>
                {showViewAll && (
                    <ViewAllWrapper>
                        <ViewAllContainer onClick={() => showAllData()}>
                            <ViewAllText>View all</ViewAllText>

                            <Image
                                src={resolvePath(
                                    '/images/seo/Chevron-down.svg'
                                )}
                                width={20}
                                height={20}
                                alt="Chevron-down"
                            />
                        </ViewAllContainer>
                    </ViewAllWrapper>
                )}
            </OurServiceCentresContainer>
        </OurServiceCentresWrapper>
    );
};

export default OurServiceCentres;

const OurServiceCentresWrapper = styled.div<{ isComingSoon?: boolean }>`
    display: flex;
    padding: 20px 16px;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    ${({ isComingSoon }) => !isComingSoon && `background: #fff;`}
`;

const OurServiceCentresContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
`;

const OurServiceCentresHeading = styled.h2`
    color: #000;
    font-family: 'Euclid Circular B';
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: -0.1px;
    margin: 0;
`;

const OurServiceCentresCardsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
`;

const OurServiceCentresCardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    border-radius: 12px;
    background: #f5f5f5;
`;

const OurServiceCentresCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    border-radius: 12px;
    border: 1px solid #ebaedc;
    background: #fff;
    overflow: hidden;
`;

const ServiceCentreDirectionWrapper = styled.div`
    display: flex;
    padding: 8px 0px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
`;

const ServiceCentreDirectionContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ServiceCentreDirectionText = styled.div`
    color: #1b73e8;
    text-align: center;
    font-family: 'Euclid Circular B';
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
`;

const TopPart = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    align-self: stretch;
    background: #deeefe;
    position: relative;
    width: 100%;
    height: 152px;
`;

const BottomPart = styled.a`
    text-decoration: none;
    display: flex;
    padding: 16px;
    justify-content: space-between;
    align-items: flex-start;
    align-self: stretch;
    background: #040222;
`;

const BottomPartLeft = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    width: calc(100% - 24px);
    flex: 1 0 0;
`;

const LocationWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 4px;

    & > span {
        flex: 0 0 auto;
    }
`;

const Location = styled.div`
    color: #fff;
    font-family: 'Euclid Circular B';
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
`;

const AddressWrapper = styled.div`
    display: flex;
    width: 300px;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
`;

const Address = styled.div<{ isComingSoon?: boolean }>`
    color: #fff;
    font-family: 'Euclid Circular B';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    opacity: 0.8;
    ${({ isComingSoon }) => !isComingSoon && `align-self: stretch;`}
`;

const ViewAllWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    align-self: stretch;
`;

const ViewAllContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const ViewAllText = styled.div`
    color: #1b73e8;
    font-family: 'Euclid Circular B';
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
`;
